/**
 * A collection of utility functions for common tasks such as file handling, date formatting, scrolling, and more.
 * 
 * @module Utils
 */

/**
 * Extracts the file extension from a filename.
 * 
 * @function
 * @param {string} filename - The filename from which to extract the extension.
 * @returns {string} The file extension.
 * @example
 * const extension = getExtension("example.jpg"); // Returns "jpg"
 */
const getExtension = (filename) => {
  const parts = filename.split(".");
  return parts[parts.length - 1];
};

/**
 * Formats a date from US format (YYYY-MM-DD) to a more readable format (DD/MM/YYYY).
 * 
 * @function
 * @param {string} us_format - The date in US format (YYYY-MM-DD).
 * @returns {string} The formatted date (DD/MM/YYYY).
 * @example
 * const formattedDate = formatDate("2023-10-05"); // Returns "05/10/2023"
 */
const formatDate = (us_format) => {
  const date = new Date(us_format)

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return [day, month, year].join('/');
}

/**
 * Determines the MIME type of a file based on its filename or path.
 * 
 * @function
 * @param {string} filename - The filename or path of the file.
 * @returns {string} The MIME type category ("img", "video", "audio", or "file").
 * @example
 * const mimeType = checkMime("example.mp4"); // Returns "video"
 */
const checkMime = (filename) => {
  let parts = filename.split("/");
  if(/audio(.*).mp4/.test(parts[parts.length - 1])){
    return "audio";
  }

  const ext = getExtension(filename);
  switch (ext.toLowerCase()) {
    case "jpg":
    case "jpeg":
    case "gif":
    case "bmp":
    case "png":
      return "img";
    case "mp4":
    case "mpg":
    case "avi":
    case "mov":
      return "video";
    case "au":
    case "snd":
    case "mid":
    case "wma":
    case "aac":
    case "mpeg":
    case "mp3":
    case "wav":
    case "m3u":
    case "webm":
      return "audio";
    default:
      return "file";
  }
};

/**
 * Scrolls a container to the bottom smoothly after a specified timeout.
 * 
 * @function
 * @param {HTMLElement} target - The container element to scroll.
 * @param {number} [timeout=612] - The delay before scrolling in milliseconds.
 * @example
 * scrollDownContainer(document.getElementById("chat-container"), 1000);
 */
const scrollDownContainer = (target, timeout)=>{
  setTimeout(function (){
    target.scroll({ top: target.scrollHeight, behavior: "smooth" });
  },0);
  setTimeout(function (){
    target.scroll({ top: target.scrollHeight, behavior: "smooth" });
  },timeout || 612);
}

/**
 * Determines the type of device based on the user agent string.
 * 
 * @function
 * @returns {string} The device type ("Android", "iPhone", or "Desktop").
 * @example
 * const deviceType = checkDeviceType(); // Returns "Desktop" on a desktop browser
 */
const checkDeviceType = () => {
  if (
    navigator.userAgent.match(/Android/i) ||
    navigator.userAgent.match(/webOS/i) ||
    navigator.userAgent.match(/BlackBerry/i) ||
    navigator.userAgent.match(/Windows Phone/i) 
    // ||
    // (navigator.platform === "MacIntel" &&
    //   navigator.maxTouchPoints > 1 &&
    //   !window.MSStream)
  ) {
    return "Android";
  }else if (
      navigator.userAgent.match(/iPhone/i) ||
      navigator.userAgent.match(/iPad/i) ||
      navigator.userAgent.match(/iPod/i) ||
      (navigator.platform === "MacIntel" &&
          navigator.maxTouchPoints > 1 &&
          !window.MSStream)
  ) {
    return "iPhone";
  } else {
    return "Desktop";
  }
};

/**
 * Prevents scrolling on mobile devices for 0.6 seconds and resets the scroll position to the top.
 * 
 * @function
 * @example
 * preventScrolling();
 */
const preventScrolling = () => {
  if (checkDeviceType() !== "Desktop") {
    window.scrollTo({
      top: 0,
    });
    window.document.body.style = "overflow-y:hidden!important;touch-action: none!important";
    setTimeout(() => {
      window.document.body.style = "overflow-y:scroll!important";
    }, 600);
  }
};

/**
 * Generates a CSS class for a flag icon based on the country code.
 * 
 * @function
 * @param {string} countryCode - The country code (e.g., "US", "ES").
 * @returns {string} The CSS class for the flag icon.
 * @example
 * const flagClass = getFlagIcon("US"); // Returns "fi fis fi-us"
 */
const getFlagIcon = (countryCode) => {
  return "fi fis fi-" + countryCode.toLowerCase();
}

/**
 * Focuses on an input element after a specified delay.
 * 
 * @function
 * @param {string} clazz - The class name of the input element.
 * @param {number} [millis=300] - The delay before focusing in milliseconds.
 * @example
 * focusInput("search-input", 500);
 */
const focusInput = (clazz, millis=300) => {
  setTimeout(function (){
    const input = document.querySelector("." + clazz);
    input.focus();
  }, millis);
}

/**
 * Replaces placeholders in a URL path with provided arguments.
 * 
 * @function
 * @param {string} path - The URL path with placeholders (e.g., "/users/:id").
 * @param {...any} args - The values to replace the placeholders.
 * @returns {string} The parsed URL path.
 * @example
 * const url = parseURLParams("/users/:id", 123); // Returns "/users/123"
 */
const parseURLParams = (path, ...args) => {
  let count = -1;
  return path.replace(/:[a-zA-Z?]+/g, function (match) {
      count += 1;
      return args[count] !== undefined ? args[count] : match;
  });
}

export { getExtension, checkMime, checkDeviceType, preventScrolling, scrollDownContainer, formatDate, getFlagIcon, focusInput, parseURLParams };
